import React, { useMemo } from 'react';
import {
    CartesianGrid, XAxis, YAxis, Tooltip,
    ResponsiveContainer, Legend, BarChart, Bar, Cell,
    PieChart, Pie,
} from 'recharts';
import { Country } from 'country-state-city';
import { useTheme } from '../../../context/ThemeContext';
import styles from '../DashboardPage.module.css';
import * as dashboardApi from '../../../api/dashboardApi';
import { BRAND_ORANGE } from '../../../design-system/chartColors';

// Distinguishable categorical palette for the stacked "by type" breakdown.
// Distinct hues (not a rainbow) drawn from the Untitled-UI family the design
// tokens are built on, so categories are easy to tell apart yet stay cohesive.
const SERIES_COLORS = [BRAND_ORANGE, '#15b79e', '#f79009', '#7a5af8', '#12b76a', '#f63d68'];
// Ranked horizontal bars encode a single metric — magnitude is read from bar
// LENGTH, so a solid brand indigo is clearest (no fading-to-white ramp).
const BAR_INDIGO = BRAND_ORANGE;

// Full-form labels for abbreviated regulation/document types
const TYPE_LABEL_MAP: Record<string, string> = {
    hr: 'House Bill',
    hres: 'House Resolution',
    s: 'Senate Bill',
    sres: 'Senate Resolution',
    hjres: 'House Joint Resolution',
    sjres: 'Senate Joint Resolution',
    hconres: 'House Concurrent Resolution',
    sconres: 'Senate Concurrent Resolution',
    bill: 'Bill',
    regulation: 'Regulation',
    guidance: 'Guidance',
    policy: 'Policy',
    framework: 'Framework',
    directive: 'Directive',
    order: 'Executive Order',
    act: 'Act',
    rule: 'Rule',
    notice: 'Notice',
    amendment: 'Amendment',
    circular: 'Circular',
    gazette: 'Gazette',
    ordinance: 'Ordinance',
    decree: 'Decree',
    resolution: 'Resolution',
    txt: 'Text Document',
    pdf: 'PDF Document',
};

function formatTypeLabel(key: string): string {
    const lower = key.toLowerCase().replace(/[_\s]+/g, ' ').trim();
    if (TYPE_LABEL_MAP[lower]) return TYPE_LABEL_MAP[lower];
    // Strip country prefix e.g. "india_bill" → "bill", "us_hr" → "hr"
    const parts = lower.split(' ');
    if (parts.length > 1 && TYPE_LABEL_MAP[parts[parts.length - 1]]) return TYPE_LABEL_MAP[parts[parts.length - 1]];
    return lower.replace(/\b\w/g, c => c.toUpperCase());
}

const SUPRANATIONALS = new Set(['EU', 'UN', 'OECD', 'GCC', 'ASEAN', 'APEC', 'AU', 'WTO', 'IMF', 'NATO', 'WHO']);
const SUPRA_NAMES: Record<string, string> = {
    EU: 'European Union', UN: 'United Nations', OECD: 'OECD',
    GCC: 'Gulf Cooperation Council', ASEAN: 'ASEAN', APEC: 'APEC',
    AU: 'African Union', WTO: 'World Trade Organization',
};

// ISO 3166-1 alpha-2 → continent (AF = Afghanistan → Asia, not Africa)
const ISO_TO_CONTINENT: Record<string, string> = {
    // Africa
    AO:'Africa',BJ:'Africa',BW:'Africa',BF:'Africa',BI:'Africa',CM:'Africa',CV:'Africa',CF:'Africa',TD:'Africa',KM:'Africa',CG:'Africa',CD:'Africa',CI:'Africa',DJ:'Africa',EG:'Africa',GQ:'Africa',ER:'Africa',ET:'Africa',GA:'Africa',GM:'Africa',GH:'Africa',GN:'Africa',GW:'Africa',KE:'Africa',LS:'Africa',LR:'Africa',LY:'Africa',MG:'Africa',MW:'Africa',ML:'Africa',MR:'Africa',MU:'Africa',MA:'Africa',MZ:'Africa',NA:'Africa',NE:'Africa',NG:'Africa',RW:'Africa',ST:'Africa',SN:'Africa',SC:'Africa',SL:'Africa',SO:'Africa',ZA:'Africa',SS:'Africa',SD:'Africa',SZ:'Africa',TZ:'Africa',TG:'Africa',TN:'Africa',UG:'Africa',ZM:'Africa',ZW:'Africa',DZ:'Africa',EH:'Africa',RE:'Africa',YT:'Africa',SH:'Africa',
    // Americas
    AG:'Americas',AR:'Americas',BS:'Americas',BB:'Americas',BZ:'Americas',BO:'Americas',BR:'Americas',CA:'Americas',CL:'Americas',CO:'Americas',CR:'Americas',CU:'Americas',DM:'Americas',DO:'Americas',EC:'Americas',SV:'Americas',GD:'Americas',GT:'Americas',GY:'Americas',HT:'Americas',HN:'Americas',JM:'Americas',MX:'Americas',NI:'Americas',PA:'Americas',PY:'Americas',PE:'Americas',KN:'Americas',LC:'Americas',VC:'Americas',SR:'Americas',TT:'Americas',US:'Americas',UY:'Americas',VE:'Americas',AI:'Americas',AW:'Americas',BQ:'Americas',CW:'Americas',FK:'Americas',GF:'Americas',GL:'Americas',GP:'Americas',KY:'Americas',MQ:'Americas',MS:'Americas',PR:'Americas',SX:'Americas',TC:'Americas',VG:'Americas',VI:'Americas',PM:'Americas',
    // Asia (AF = Afghanistan)
    AF:'Asia',AM:'Asia',AZ:'Asia',BH:'Asia',BD:'Asia',BT:'Asia',BN:'Asia',KH:'Asia',CN:'Asia',CY:'Asia',GE:'Asia',IN:'Asia',ID:'Asia',IR:'Asia',IQ:'Asia',IL:'Asia',JP:'Asia',JO:'Asia',KZ:'Asia',KW:'Asia',KG:'Asia',LA:'Asia',LB:'Asia',MY:'Asia',MV:'Asia',MN:'Asia',MM:'Asia',NP:'Asia',KP:'Asia',OM:'Asia',PK:'Asia',PS:'Asia',PH:'Asia',QA:'Asia',SA:'Asia',SG:'Asia',KR:'Asia',LK:'Asia',SY:'Asia',TW:'Asia',TJ:'Asia',TH:'Asia',TL:'Asia',TR:'Asia',TM:'Asia',AE:'Asia',UZ:'Asia',VN:'Asia',YE:'Asia',HK:'Asia',MO:'Asia',
    // Europe
    AL:'Europe',AD:'Europe',AT:'Europe',BY:'Europe',BE:'Europe',BA:'Europe',BG:'Europe',HR:'Europe',CZ:'Europe',DK:'Europe',EE:'Europe',FI:'Europe',FR:'Europe',DE:'Europe',GR:'Europe',HU:'Europe',IS:'Europe',IE:'Europe',IT:'Europe',LV:'Europe',LI:'Europe',LT:'Europe',LU:'Europe',MT:'Europe',MD:'Europe',MC:'Europe',ME:'Europe',NL:'Europe',MK:'Europe',NO:'Europe',PL:'Europe',PT:'Europe',RO:'Europe',RU:'Europe',SM:'Europe',RS:'Europe',SK:'Europe',SI:'Europe',ES:'Europe',SE:'Europe',CH:'Europe',UA:'Europe',GB:'Europe',VA:'Europe',AX:'Europe',FO:'Europe',GI:'Europe',GG:'Europe',IM:'Europe',JE:'Europe',SJ:'Europe',XK:'Europe',
    // Oceania
    AU:'Oceania',FJ:'Oceania',KI:'Oceania',MH:'Oceania',FM:'Oceania',NR:'Oceania',NZ:'Oceania',PW:'Oceania',PG:'Oceania',WS:'Oceania',SB:'Oceania',TO:'Oceania',TV:'Oceania',VU:'Oceania',AS:'Oceania',CK:'Oceania',GU:'Oceania',NC:'Oceania',NF:'Oceania',MP:'Oceania',PF:'Oceania',PN:'Oceania',TK:'Oceania',WF:'Oceania',
};

// Distinct categorical hues per continent so donut segments are easy to tell
// apart (matches the by-type palette family); neutral grays for non-regions.
const CONTINENT_COLORS: Record<string, string> = {
    Americas:     BRAND_ORANGE,
    Asia:         '#15b79e',
    Europe:       '#7a5af8',
    Africa:       '#f79009',
    Oceania:      '#12b76a',
    Supranational:'#667085',
    Other:        '#98a2b3',
};

function getContinent(code: string): string {
    return ISO_TO_CONTINENT[code] ?? 'Other';
}

function getContinentColor(name: string): string {
    return CONTINENT_COLORS[name] ?? '#98a2b3';
}

// ── Premium shared styles (theme-aware via CSS design tokens) ──────────────
const cardStyle: React.CSSProperties = {
    padding: '1.75rem',
    background: 'var(--ds-surface)',
    border: '1px solid var(--ds-border)',
    borderRadius: 'var(--ds-radius-xl)',
    boxShadow: 'var(--ds-shadow-1)',
};
const eyebrowStyle: React.CSSProperties = {
    margin: '0 0 1.5rem 0',
    fontFamily: 'var(--ds-font-mono)',
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--ds-text-faint)',
};
const statLabelStyle: React.CSSProperties = {
    fontFamily: 'var(--ds-font-mono)',
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--ds-text-faint)',
    marginBottom: '0.6rem',
};
const numeralStyle: React.CSSProperties = {
    fontFamily: 'var(--ds-font-display)',
    fontWeight: 600,
    fontSize: '2.25rem',
    lineHeight: 1,
    letterSpacing: '-0.015em',
    color: 'var(--ds-text)',
};
const subValueStyle: React.CSSProperties = {
    fontFamily: 'var(--ds-font-display)',
    fontSize: '1.125rem',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: 'var(--ds-text)',
};

const TrendChart: React.FC<{
    data: dashboardApi.TrendDataPoint[];
    isLoading: boolean;
    totalRegulations?: number;
    jurisdictionStats?: dashboardApi.JurisdictionStats[];
    countryStats?: Record<string, number>;
    hideBreakdown?: boolean;
    hideCountries?: boolean;
}> = ({ data, isLoading, totalRegulations, jurisdictionStats = [], countryStats = {}, hideBreakdown = false, hideCountries = false }) => {
    const { theme } = useTheme();
    const strokeColor = theme === 'dark' ? '#d0d5dd' : '#475467';
    const gridColor = theme === 'dark' ? '#344054' : '#eaecf0';
    const bgColor = theme === 'dark' ? '#1d2939' : '#fff';
    const borderColor = theme === 'dark' ? '#344054' : '#eaecf0';

    const shouldShowYearly = data.length > 12;

    const chartData: any[] = useMemo(() => {
        if (shouldShowYearly && data.length > 0) {
            const yearlyData = new Map<string, { regulations: number; types: Record<string, number> }>();
            data.forEach(point => {
                const year = point.period.split(' ')[1] || point.period;
                if (!yearlyData.has(year)) yearlyData.set(year, { regulations: 0, types: {} });
                const yearData = yearlyData.get(year)!;
                yearData.regulations += point.regulations_count;
                Object.entries(point.by_type).forEach(([type, count]) => {
                    yearData.types[type] = (yearData.types[type] || 0) + (count as number);
                });
            });
            return Array.from(yearlyData.entries()).map(([year, d]) => ({
                name: year,
                regulations: d.regulations,
                ...Object.fromEntries(Object.entries(d.types).slice(0, 6)),
            }));
        }
        return data.map(point => ({
            name: point.period,
            regulations: point.regulations_count,
            ...Object.fromEntries(Object.entries(point.by_type).slice(0, 6)),
        }));
    }, [data, shouldShowYearly]);

    // Collect type keys for stacked bar
    const typeKeys = useMemo(() => {
        const keys = new Set<string>();
        chartData.forEach(d => {
            Object.keys(d).forEach(k => { if (k !== 'name' && k !== 'regulations') keys.add(k); });
        });
        return Array.from(keys).slice(0, 6);
    }, [chartData]);

    // Top 10 jurisdictions
    const topJurisdictions = useMemo(() => {
        return [...jurisdictionStats]
            .sort((a, b) => b.regulation_count - a.regulation_count)
            .slice(0, 10)
            .map(s => ({ name: s.jurisdiction_name || s.jurisdiction_code, count: s.regulation_count }));
    }, [jurisdictionStats]);

    // Country-wise chart data
    const countryChartData = useMemo(() => {
        return Object.entries(countryStats)
            .map(([code, count]) => {
                if (SUPRANATIONALS.has(code)) return { name: SUPRA_NAMES[code] || code, count, code };
                const country = Country.getCountryByCode(code);
                if (!country) return null; // drop unknown codes like XE
                return { name: country.name, count, code };
            })
            .filter((x): x is { name: string; count: number; code: string } => x !== null)
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }, [countryStats]);

    // Continent pie chart data
    const continentData = useMemo(() => {
        const totals: Record<string, number> = {};
        Object.entries(countryStats).forEach(([code, count]) => {
            if (SUPRANATIONALS.has(code)) {
                totals['Supranational'] = (totals['Supranational'] || 0) + count;
                return;
            }
            const continent = getContinent(code);
            totals[continent] = (totals[continent] || 0) + count;
        });
        return Object.entries(totals)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [countryStats]);

    if (isLoading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--ds-text-muted)', fontFamily: 'var(--ds-font-mono)', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Loading trends...
        </div>
    );

    if (data.length === 0 && countryChartData.length === 0) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--ds-text-muted)', fontFamily: 'var(--ds-font-mono)', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            No data available.
        </div>
    );

    const tooltipStyle = {
        contentStyle: {
            backgroundColor: bgColor,
            borderColor,
            borderRadius: '12px',
            border: '1px solid',
            padding: '12px 14px',
            boxShadow: '0 12px 16px -4px rgba(16,24,40,.08), 0 4px 6px -2px rgba(16,24,40,.03)',
        },
        labelStyle: { color: strokeColor, fontWeight: 600, fontFamily: "'Inter', sans-serif" },
        itemStyle: { fontSize: '12px', fontFamily: "'Inter', sans-serif" },
        cursor: { fill: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(16,24,40,0.03)' },
    };

    return (
        <div className={styles.chartContainer}>

            {/* Trends Summary */}
            {data.length > 0 && (
                <div style={cardStyle}>
                    <h3 style={eyebrowStyle}>Trends Summary</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.75rem' }}>
                        <div>
                            <div style={statLabelStyle}>Total Regulations</div>
                            <div style={{ ...numeralStyle, color: 'var(--ds-link)' }}>
                                {(typeof totalRegulations === 'number' ? totalRegulations : data.reduce((sum, p) => sum + p.regulations_count, 0)).toLocaleString()}
                            </div>
                        </div>
                        <div>
                            <div style={statLabelStyle}>Period</div>
                            <div style={subValueStyle}>{data[0]?.period} to {data[data.length - 1]?.period}</div>
                        </div>
                        <div>
                            <div style={statLabelStyle}>Average per Period</div>
                            <div style={numeralStyle}>
                                {Math.round(data.reduce((sum, p) => sum + p.regulations_count, 0) / data.length).toLocaleString()}
                            </div>
                        </div>
                        <div>
                            <div style={statLabelStyle}>Peak Period</div>
                            <div style={subValueStyle}>
                                {data.reduce((max, p) => p.regulations_count > max.regulations_count ? p : max).period}
                            </div>
                        </div>
                        <div>
                            <div style={statLabelStyle}>First to Last</div>
                            {(() => {
                                const first = data[0].regulations_count;
                                const last = data[data.length - 1].regulations_count;
                                const change = last - first;
                                const percent = ((change / (first || 1)) * 100).toFixed(1);
                                const positive = change >= 0;
                                return (
                                    <div style={{ ...subValueStyle, color: positive ? 'var(--ds-success)' : 'var(--ds-danger)' }}>
                                        {positive ? '+' : ''}{change} ({percent}%)
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Breakdown by Type + Top Jurisdictions */}
            <div style={{ display: 'grid', gridTemplateColumns: hideBreakdown ? '1fr' : '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>

                {/* Stacked Bar: breakdown by regulation type */}
                {!hideBreakdown && typeKeys.length > 0 && (
                    <div style={cardStyle}>
                        <h3 style={eyebrowStyle}>Breakdown by Type</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barCategoryGap="22%">
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} opacity={0.5} />
                                <XAxis dataKey="name" axisLine={{ stroke: gridColor }} tickLine={false} tick={{ fill: strokeColor, fontSize: 11 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: strokeColor, fontSize: 11 }} dx={-6} />
                                <Tooltip {...tooltipStyle} formatter={(value: any, name: string | undefined) => [value, formatTypeLabel(name ?? '')]} />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', paddingTop: '12px' }} formatter={(value) => formatTypeLabel(value)} />
                                {typeKeys.map((key, i) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        stackId="a"
                                        fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                                        name={key}
                                        maxBarSize={44}
                                        radius={i === typeKeys.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Horizontal Bar: top jurisdictions */}
                {topJurisdictions.length > 0 && (
                    <div style={cardStyle}>
                        <h3 style={eyebrowStyle}>Top Jurisdictions</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart layout="vertical" data={topJurisdictions} margin={{ top: 0, right: 24, left: 0, bottom: 0 }} barCategoryGap="28%">
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} opacity={0.5} />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: strokeColor, fontSize: 11 }} />
                                <YAxis type="category" dataKey="name" width={110} axisLine={false} tickLine={false} tick={{ fill: strokeColor, fontSize: 11 }} />
                                <Tooltip {...tooltipStyle} />
                                <Bar dataKey="count" name="Regulations" radius={[0, 4, 4, 0]} maxBarSize={18}>
                                    {topJurisdictions.map((_, i) => (
                                        <Cell key={i} fill={BAR_INDIGO} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Continent pie chart */}
            {continentData.length > 0 && (() => {
                const total = continentData.reduce((s, d) => s + d.value, 0);
                return (
                    <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
                        <h3 style={eyebrowStyle}>Regulations by Continent</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2.5rem', flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative', width: 300, height: 300 }}>
                            <ResponsiveContainer width={300} height={300}>
                                <PieChart>
                                    <Pie
                                        data={continentData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={120}
                                        innerRadius={78}
                                        paddingAngle={2}
                                        cornerRadius={5}
                                        stroke="none"
                                    >
                                        {continentData.map((entry) => (
                                            <Cell key={entry.name} fill={getContinentColor(entry.name)} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0];
                                            const pct = total ? (((d.value as number) / total) * 100).toFixed(1) : '0';
                                            return (
                                                <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 12, padding: '12px 14px', fontSize: 12, boxShadow: '0 12px 16px -4px rgba(16,24,40,.08), 0 4px 6px -2px rgba(16,24,40,.03)', fontFamily: "'Inter', sans-serif" }}>
                                                    <div style={{ fontWeight: 600, color: strokeColor, marginBottom: 3 }}>{d.name}</div>
                                                    <div style={{ color: strokeColor }}>{(d.value as number).toLocaleString()} regulations</div>
                                                    <div style={{ color: 'var(--ds-text-muted)', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{pct}% of total</div>
                                                </div>
                                            );
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                <div style={{ fontFamily: 'var(--ds-font-display)', fontWeight: 600, fontSize: '2rem', lineHeight: 1, letterSpacing: '-0.015em', color: 'var(--ds-text)' }}>
                                    {total.toLocaleString()}
                                </div>
                                <div style={{ fontFamily: 'var(--ds-font-mono)', fontSize: '0.625rem', fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--ds-text-faint)', marginTop: 8 }}>
                                    Total
                                </div>
                            </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', flex: 1, minWidth: 180 }}>
                                {continentData.map((entry) => {
                                    const pct = total ? ((entry.value / total) * 100).toFixed(1) : '0';
                                    return (
                                        <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', fontSize: 13, padding: '0.45rem 0', borderBottom: '1px solid var(--ds-border)' }}>
                                            <span style={{ width: 10, height: 10, borderRadius: 3, background: getContinentColor(entry.name), flexShrink: 0 }} />
                                            <span style={{ color: 'var(--ds-text-secondary)', flex: 1, fontFamily: "'Inter', sans-serif" }}>{entry.name}</span>
                                            <span style={{ fontFamily: 'var(--ds-font-display)', fontWeight: 600, fontSize: '1rem', color: 'var(--ds-text)' }}>{entry.value.toLocaleString()}</span>
                                            <span style={{ fontFamily: 'var(--ds-font-mono)', color: 'var(--ds-text-faint)', fontSize: 11, minWidth: 46, textAlign: 'right' }}>{pct}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Country-wise chart */}
            {!hideCountries && countryChartData.length > 0 && (
                <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
                    <h3 style={eyebrowStyle}>Top 10 Countries by Regulations</h3>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart layout="vertical" data={countryChartData} margin={{ top: 0, right: 32, left: 0, bottom: 0 }} barCategoryGap="28%">
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} opacity={0.5} />
                            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: strokeColor, fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" width={150} axisLine={false} tickLine={false} tick={{ fill: strokeColor, fontSize: 11 }} />
                            <Tooltip {...tooltipStyle} />
                            <Bar dataKey="count" name="Regulations" radius={[0, 4, 4, 0]} maxBarSize={18}>
                                {countryChartData.map((_, i) => (
                                    <Cell key={i} fill={BAR_INDIGO} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default TrendChart;
